import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { UserService } from 'src/app/services/user/user.service';

interface FloatingServiceItem {
  label: string;
  icon: string;
  route: string[];
}

@Component({
  selector: 'app-floating-services',
  templateUrl: './floating-services.component.html',
  styleUrls: ['./floating-services.component.css'],
})
export class FloatingServicesComponent implements OnInit, OnDestroy {
  isOpen = false;
  isCompanyUser = false;
  private readonly isBrowser: boolean;

  readonly serviceItems: FloatingServiceItem[] = [
    { label: 'Career Coaching', icon: 'bi-briefcase', route: ['/careerCoach'] },
    { label: 'Scan CV', icon: 'bi-file-earmark-medical', route: ['/scan-cv'] },
    { label: 'Create Resume', icon: 'bi-file-earmark-text', route: ['/generatResume'] },
    { label: 'Cover Letter', icon: 'bi-file-earmark-richtext', route: ['/create-cover-letter'] },
    { label: 'LinkedIn Profile', icon: 'bi-linkedin', route: ['/create-linkedin-profile'] },
    { label: 'Interview', icon: 'bi-camera-video', route: ['/virtual-interview'] },
  ];

  private readonly itemLayout = [
    { right: Math.round(215 * Math.cos(0 * Math.PI / 180) + 5), bottom: Math.round(215 * Math.sin(0 * Math.PI / 180) + 5) },  // θ=  0°
    { right: Math.round(215 * Math.cos(18 * Math.PI / 180) + 5), bottom: Math.round(215 * Math.sin(18 * Math.PI / 180) + 5) },  // θ= 18°
    { right: Math.round(215 * Math.cos(36 * Math.PI / 180) + 5), bottom: Math.round(215 * Math.sin(36 * Math.PI / 180) + 5) },  // θ= 36°
    { right: Math.round(215 * Math.cos(54 * Math.PI / 180) + 5), bottom: Math.round(215 * Math.sin(54 * Math.PI / 180) + 5) },  // θ= 54°
    { right: Math.round(215 * Math.cos(72 * Math.PI / 180) + 5), bottom: Math.round(215 * Math.sin(72 * Math.PI / 180) + 5) },  // θ= 72°
    { right: Math.round(215 * Math.cos(90 * Math.PI / 180) + 5), bottom: Math.round(215 * Math.sin(90 * Math.PI / 180) + 5) },  // θ= 90°
  ];


  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private userService: UserService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.syncRole();

    this.userService
      .getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncRole());

    this.userService
      .getLoginStatus$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncRole());

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isOpen = false;
      });

    if (this.isBrowser) {
      window.addEventListener('storage', this.handleStorageChange);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.isBrowser) {
      window.removeEventListener('storage', this.handleStorageChange);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.floating-services')) {
      this.isOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen = false;
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isCompanyUser) return;
    this.isOpen = !this.isOpen;
  }

  navigate(route: string[], event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen = false;
    this.router.navigate(route);
  }

  getItemRight(index: number): number {
    return this.itemLayout[index]?.right ?? 74;
  }

  getItemBottom(index: number): number {
    return this.itemLayout[index]?.bottom ?? 0;
  }

  isActiveRoute(route: string[]): boolean {
    if (!this.isBrowser) return false;
    const url = this.router.url.split('?')[0];
    return url === route[0] || url.startsWith(route[0] + '/');
  }

  private syncRole(): void {
    if (!this.isBrowser) {
      this.isCompanyUser = false;
      this.isOpen = false;
      return;
    }

    const userInfo = this.userService.getUserInfo();
    const roleRaw = userInfo?.role || localStorage.getItem('role');
    const roleName =
      typeof roleRaw === 'object' && roleRaw !== null ? roleRaw.name : roleRaw;

    this.isCompanyUser = roleName?.toUpperCase() === 'COMPANY';

    if (this.isCompanyUser) {
      this.isOpen = false;
    }
  }

  private handleStorageChange = (): void => {
    this.syncRole();
  };
}
